import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { SafeUrl } from "@angular/platform-browser";
import { Router, RouterModule } from "@angular/router";
import { ImgFallbackDirective } from "@shared/directives/img-fallback/img-fallback.directive";
import { debounceTime, Subject, switchMap, takeUntil, tap } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { UtilsService } from "../../../../../utils/utils.service";
import { SnackBarService } from "../../../../services/snack-bar.service";
import { StarredEventsService } from "../../../../services/starred-events.service";
import { EventInfoResponseBody } from "../../../../services/types/events-management-response.type";

@Component({
  selector: "app-event-card",
  imports: [
    CommonModule,
    RouterModule,
    ButtonComponent,
    MatIconModule,
    MatButtonModule,
    ImgFallbackDirective,
  ],
  templateUrl: "./event-card.component.html",
  styleUrl: "./event-card.component.scss",
})
export class EventCardComponent implements OnChanges, OnInit, OnDestroy {
  @Input({ required: true }) event!: EventInfoResponseBody;
  @Input() isProducerArea: boolean = false;
  @Input() showEditButton: boolean = false;
  @Input() fullWidth: boolean = false;
  eventImage!: SafeUrl | undefined;
  isAnimating = false;

  isFavorite = false;

  private favoriteToggle$ = new Subject<{
    eventId: string;
  }>();
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private readonly utilsService: UtilsService,
    private readonly snackbarService: SnackBarService,
    private readonly starredEventsService: StarredEventsService,
  ) {}

  ngOnInit(): void {
    this.favoriteToggle$
      .pipe(
        debounceTime(400),
        takeUntil(this.destroy$),
        switchMap(({ eventId }) =>
          this.starredEventsService.updateStarredEvent(Number(eventId)),
        ),
        tap((response) => {
          if (response) {
            this.event.is_favorite = !this.event.is_favorite;
          }
        }),
      )
      .subscribe();
  }

  ngOnChanges(): void {
    this.loadEventImage(this.event.thumbnail);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  eventDateHasPassed(eventDate: string) {
    return this.utilsService.isPast(eventDate);
  }

  goToProducerEventPage() {
    this.router.navigate([`/producer-area/event/${this.event.id}`]);
  }

  goToEventPage() {
    this.router.navigate([`/event/${this.event.id}`]);
  }

  editEvent() {
    const isPast = this.utilsService.isPast(this.event.end_date);
    if (isPast) {
      this.snackbarService.openSnackBar(
        "O evento já acabou, não é possivel editar.",
        "Fechar",
        "error",
      );
    } else {
      this.router.navigateByUrl(`/edit-event/${this.event.id}`);
    }
  }

  handleFavoriteClick(event: MouseEvent) {
    event.stopPropagation();

    this.favoriteToggle$.next({
      eventId: this.event.id.toString(),
    });

    this.animateFavoriteIcon();
  }

  animateFavoriteIcon() {
    this.isAnimating = false;

    requestAnimationFrame(() => {
      this.isAnimating = true;

      setTimeout(() => {
        this.isAnimating = false;
      }, 300);
    });
  }

  getEventStatus() {
    const isPast = this.utilsService.isPast(this.event.end_date);
    if (isPast) {
      return "Encerrado";
    }
    switch (this.event.status) {
      case "published":
        return "Publicado";
      case "draft":
        return "Rascunho";
      case "cancelled":
        return "Cancelado";
      case "pending_approval":
        return "Pendente Aprovação";
      default:
        return this.event.status;
    }
  }

  async loadEventImage(event_thumb: string) {
    if (event_thumb !== "" && event_thumb !== "FALTA IMPLEMENTAR") {
      this.eventImage = `${environment.BLOB_URL}/${event_thumb}`;
    }
  }
}
